import { useState } from "react";
import { Play, Pause, Volume2, ArrowRight, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";

export default function WelcomeStep({ onNext }) {
  const [playing, setPlaying] = useState(false);

  return (
    <div data-testid="onboarding-welcome" className="reveal-child grid grid-cols-12 gap-8 items-center">
      <div className="col-span-12 md:col-span-5 space-y-5">
        <Logo size={44} />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Tu kit de bienvenida
        </p>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.92]">
          Bienvenido a <br />
          <span className="italic text-coral">Ovamx</span>.
        </h1>
        <p className="text-base md:text-lg text-ink/80 leading-relaxed text-balance">
          Aquí tu tiempo tiene valor, tu identidad se respeta y tu creatividad se paga. Tu guía Ovamx te explica todo en 60 segundos.
        </p>
        <button
          onClick={onNext}
          data-testid="welcome-next"
          className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-7 py-3.5 text-sm hover:bg-coral transition-colors duration-500 group"
        >
          Empezar el tour
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      <div className="col-span-12 md:col-span-7">
        <div
          data-testid="welcome-video"
          className="relative aspect-video rounded-2xl overflow-hidden border hairline bg-ink shadow-2xl group cursor-pointer"
          onClick={() => setPlaying((p) => !p)}
        >
          {/* Animated gradient backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-ink via-ink to-coral/40" />
          <div
            className="absolute inset-0 opacity-50 mix-blend-screen"
            style={{
              background: "radial-gradient(600px 300px at 30% 30%, rgba(224,122,95,0.45) 0%, transparent 70%), radial-gradient(500px 250px at 70% 70%, rgba(127,169,214,0.35) 0%, transparent 70%)",
              animation: playing ? "shimmer 4s ease-in-out infinite" : undefined,
            }}
          />

          {/* AI avatar circle */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`relative w-32 h-32 md:w-44 md:h-44 rounded-full ring-4 ring-cream/15 overflow-hidden transition-transform duration-700 ${playing ? "scale-105" : "scale-100"}`}>
              <div className="absolute inset-0 bg-gradient-to-br from-coral via-coral-hover to-ink" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-serif text-7xl md:text-8xl tracking-tighter text-cream">O</span>
              </div>
              {/* sparkle pulse */}
              {playing && (
                <span className="absolute -top-1 right-3 text-coral animate-shimmer text-2xl">✦</span>
              )}
            </div>
          </div>

          {/* Caption */}
          <div className="absolute top-5 left-5 max-w-[55%]">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/60">Tu guía Ovamx</p>
            <p className="mt-1 font-serif text-2xl md:text-3xl text-cream leading-tight">
              «Hola, te voy a mostrar lo que hace a Ovamx diferente.»
            </p>
          </div>

          {/* Play overlay */}
          <button className="absolute bottom-5 right-5 inline-flex items-center gap-2 bg-cream/95 hover:bg-cream text-ink px-5 py-3 rounded-full text-sm transition" onClick={(e) => { e.stopPropagation(); setPlaying((p) => !p); }} data-testid="welcome-play-toggle">
            {playing ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4 translate-x-0.5" fill="currentColor" />}
            {playing ? "Pausar" : "Reproducir"}
          </button>

          {/* Bottom progress fake */}
          <div className="absolute bottom-0 inset-x-0 h-1 bg-cream/10">
            <div className={`h-full bg-coral transition-all ${playing ? "w-1/3 animate-shimmer" : "w-0"}`} />
          </div>

          <div className="absolute top-5 right-5 flex items-center gap-2 text-cream/70">
            <Volume2 className="w-4 h-4" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]">0:00 · 1:00</span>
          </div>
        </div>

        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid text-center">
          Video generado por IA · personalizado a tu idioma
        </p>
      </div>
    </div>
  );
}
